import { type FC, useMemo } from 'react'

import { useUnit } from 'effector-react'
import { Controller } from 'react-hook-form'

import IconChevronDown from '@/assets/icons/icon-chevron-down.svg'
import IconFolder from '@/assets/icons/icon-folder.svg'
import IconLoading from '@/assets/icons/icon-loading.svg'
import NewFolderIcon from '@/assets/icons/icon-new-folder.svg'
import IconWarning from '@/assets/icons/icon-warning.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import {
  $scriptCategories,
  $scriptsError,
  getScriptsFx,
} from '@/models/scripts/scripts.ts'
import { classNames } from '@/utils/helpers'

interface Props {
  className?: string
  handleCreateFolderClick: () => void
}

export const CategorySelector: FC<Props> = ({
  className,
  handleCreateFolderClick,
}) => {
  const scriptsError = useUnit($scriptsError)
  const scripts = useUnit($scriptCategories)
  const isFetching = useUnit(getScriptsFx.pending)
  const canAddFolder = useMemo(() => !!scripts, [scripts])

  return (
    <Controller
      name="category"
      defaultValue={0}
      render={function Field({ field, fieldState: { error } }) {
        const selected = useMemo(
          () => scripts.find((script) => script.id === field.value),
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [scripts, field.value],
        )

        return (
          <div className={classNames('flex flex-col gap-2', className)}>
            <label
              htmlFor="category"
              className="text-sm font-medium text-title"
            >
              Add to Folder
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex h-[40px] w-full items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-title  focus:border-primary-500 focus:outline-primary-500"
                testId="category-selector"
              >
                <IconFolder className="h-4 w-4 text-secondary-300" />
                {selected ? selected.name : 'Select Folder'}
                <IconChevronDown className="ml-auto h-4 w-4 flex-shrink-0 text-title" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="z-[100] w-64 p-2"
                align="start"
                forceMount
              >
                <DropdownMenuGroup className="flex h-64 flex-col gap-[2px] overflow-scroll">
                  {isFetching && (
                    <DropdownMenuItem className="group cursor-pointer gap-2 rounded-md text-title">
                      <IconLoading className="h-4 w-4 animate-spin text-secondary-300" />
                      Loading Folders...
                    </DropdownMenuItem>
                  )}
                  {!!scriptsError && (
                    <DropdownMenuItem className="group cursor-pointer gap-2 rounded-md text-title">
                      <IconWarning className="h-4 w-4 text-danger-default" />
                      Failed to load folders
                    </DropdownMenuItem>
                  )}
                  {scripts?.map((option, index) => (
                    <DropdownMenuItem
                      key={option.id}
                      onClick={() => field.onChange(option.id)}
                      testId={`category-option-${index}`}
                      className="group cursor-pointer gap-2 rounded-md text-title hover:bg-primary-500/20 hover:text-primary-500"
                    >
                      <IconFolder className="text-secondary-300" />
                      {option.name}
                    </DropdownMenuItem>
                  ))}
                  {canAddFolder && (
                    <DropdownMenuItem
                      onClick={handleCreateFolderClick}
                      className="group cursor-pointer gap-2 rounded-md text-title hover:bg-primary-500/20 hover:text-primary-500"
                      testId="create-folder-option"
                    >
                      <NewFolderIcon className="h-4 w-4 text-secondary-300" />
                      Create Folder
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            {error && (
              <p className="text-xs text-danger-default">{error.message}</p>
            )}
          </div>
        )
      }}
    />
  )
}
